import { createBrowserRouter, RouterProvider, Navigate, Outlet } from "react-router";
import { Toaster } from "sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/layout/AppLayout";
import LoginPage from "@/pages/LoginPage";
import AccessDeniedPage from "@/pages/AccessDeniedPage";
import DashboardPage from "@/pages/DashboardPage";
import PendingEventsPage from "@/pages/PendingEventsPage";
import AllEventsPage from "@/pages/AllEventsPage";
import CategoriesPage from "@/pages/CategoriesPage";
import UsersPage from "@/pages/UsersPage";
import OckScrapersPage from "@/pages/OckScrapersPage";
import MoviesPage from "@/pages/MoviesPage";
import VenuesPage from "@/pages/VenuesPage";
import AiSourcesPage from "@/pages/AiSourcesPage";
import AiEventsPage from "@/pages/AiEventsPage";
import AiRunsPage from "@/pages/AiRunsPage";
import ParentEventsPage from "@/pages/ParentEventsPage";
import OrganizerEventsPage from "@/pages/OrganizerEventsPage";
import OrganizerProfilePage from "@/pages/OrganizerProfilePage";
import ReportsPage from "@/pages/ReportsPage";
import SystemLogsPage from "@/pages/SystemLogsPage";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});

function AuthGuard({ allowedRoles }: { allowedRoles: string[] }) {
  const { session, userRole, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Ładowanie...</p>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (!userRole || !allowedRoles.includes(userRole)) {
    return <AccessDeniedPage />;
  }

  return <Outlet />;
}

function RoleRedirect() {
  const { session, userRole, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Ładowanie...</p>
      </div>
    );
  }

  if (!session) return <Navigate to="/login" replace />;
  if (userRole === "organizer") return <Navigate to="/my-events" replace />;
  if (userRole === "admin") return <Navigate to="/dashboard" replace />;
  return <AccessDeniedPage description="To konto nie ma dostępu do panelu." />;
}

const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { index: true, element: <RoleRedirect /> },
  {
    element: <AuthGuard allowedRoles={["admin"]} />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: "dashboard", element: <DashboardPage /> },
          { path: "pending", element: <PendingEventsPage /> },
          { path: "events", element: <AllEventsPage /> },
          { path: "parent-events", element: <ParentEventsPage /> },
          { path: "categories", element: <CategoriesPage /> },
          { path: "users", element: <UsersPage /> },
          { path: "reports", element: <ReportsPage /> },
          { path: "scrapers", element: <Navigate to="/scrapers/ock" replace /> },
          { path: "scrapers/ock", element: <OckScrapersPage /> },
          { path: "movies", element: <MoviesPage /> },
          { path: "venues", element: <VenuesPage /> },
          { path: "ai-agent", element: <Navigate to="/ai-agent/sources" replace /> },
          { path: "ai-agent/sources", element: <AiSourcesPage /> },
          { path: "ai-agent/events", element: <AiEventsPage /> },
          { path: "ai-agent/runs", element: <AiRunsPage /> },
          { path: "system-logs", element: <SystemLogsPage /> },
        ],
      },
    ],
  },
  {
    element: <AuthGuard allowedRoles={["organizer"]} />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: "my-events", element: <OrganizerEventsPage /> },
          { path: "my-profile", element: <OrganizerProfilePage /> },
        ],
      },
    ],
  },
]);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={0}>
        <AuthProvider>
          <RouterProvider router={router} />
          <Toaster richColors />
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
